import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { userportal as routes } from 'shared/routes';
import CloseButton from 'shared/components/buttons/Close';
import Score from 'shared/components/Score';
import ScoreDims from 'shared/components/Score/Dims';
import styles from './RecruiterReview.module.scss';

const VERDICT_CLASS = {
  READY: 'ready',
  STRETCH: 'stretch',
  NOT_YET: 'notYet',
};

const Skeleton = ({ lines }) => (
  <div className={styles.skeleton}>
    {Array.from({ length: lines }).map((__, i) => (
      <i key={i} />
    ))}
  </div>
);

Skeleton.propTypes = { lines: PropTypes.number };
Skeleton.defaultProps = { lines: 3 };

// The first streamed token takes ~8-9 s to arrive (see the latency note in
// CLAUDE.md), so the wait is walked through step by step instead of holding a
// single static line — same pattern as the AI job import in the backoffice.
// Durations are for the first steps only: the last one keeps spinning until the
// text arrives, because it must never look finished while it is still coming.
const STEP_DURATIONS = [1200, 1800, 2500, 2500];
const STEP_COUNT = STEP_DURATIONS.length + 1;

const LoadingSteps = () => {
  const { t } = useTranslation('userportal');
  const [step, setStep] = useState(1);

  useEffect(() => {
    const timers = [];
    STEP_DURATIONS.reduce((elapsed, duration, i) => {
      const at = elapsed + duration;
      timers.push(setTimeout(() => setStep(i + 2), at));
      return at;
    }, 0);

    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <ul className={styles.loadSteps}>
      {Array.from({ length: STEP_COUNT }).map((__, i) => {
        const s = i + 1;
        return (
          <li
            key={s}
            className={cn(styles.loadStep, {
              [styles.loadStepActive]: s === step,
              [styles.loadStepDone]: s < step,
            })}
          >
            <span className={styles.loadStepMark}>
              {s < step && <i className="la la-check-circle" />}
              {s === step && <i className={cn('spinner-border', styles.loadStepSpinner)} />}
              {s > step && <i className="la la-clock" />}
            </span>
            {t(`page.job.userJobReview.loadingSteps.${s}`)}
          </li>
        );
      })}
    </ul>
  );
};

const Section = ({ title, count, sub, tipStyle, loading, children }) => (
  <section className={cn(styles.sec, { [styles.secTip]: tipStyle })}>
    <div className={styles.secHead}>
      <h3 className={styles.secTitle}>{title}</h3>
      {count > 0 && <span className={styles.secCount}>{count}</span>}
    </div>
    {sub && <p className={styles.secSub}>{sub}</p>}
    {loading ? <Skeleton /> : children}
  </section>
);

Section.propTypes = {
  title: PropTypes.node.isRequired,
  count: PropTypes.number,
  sub: PropTypes.node,
  tipStyle: PropTypes.bool,
  loading: PropTypes.bool,
  children: PropTypes.node,
};

Section.defaultProps = {
  count: 0,
  sub: null,
  tipStyle: false,
  loading: false,
  children: null,
};

// Progressive template for the streamed "how a recruiter sees you" review.
// Every field of `review` is optional — the object grows while the LLM
// response streams in (see parsePartialJson) and the template renders whatever
// has already arrived. Enum fields (verdict, concern type) render only on an
// exact value match so a partially streamed value never picks a wrong branch.
const RecruiterReview = ({ review, done, jobId, score, scoreDims, gender, injectionDetected, injectionEvidence, onClose }) => {
  const { t } = useTranslation('userportal');

  const r = review || {};
  // on a confident prompt-injection detection the backend returns an empty
  // review; the panel switches to a dedicated red error state (no verdict) and
  // the warning wording is composed here from i18n
  const verdict = VERDICT_CLASS[r.verdict] ? r.verdict : null;
  const isNotYet = verdict === 'NOT_YET';

  const leads = r.leads || [];
  const concerns = r.concerns || [];
  const openPoints = r.open_points || [];
  const interviewPrep = r.interview_prep || [];
  const development = r.development || [];

  const tr = (key) => t(`page.job.userJobReview.${key}`);

  const headline = injectionDetected ? t('common:promptInjection.userReview.headline') : r.headline;
  const recruiterView = injectionDetected ? t('common:promptInjection.userReview.recruiterView') : r.recruiter_view;

  const renderLeads = () => (leads.length > 0 || !done) && (
    <Section
      key="leads"
      title={tr(isNotYet ? 'leadsTitleNotYet' : 'leadsTitle')}
      count={leads.length}
      sub={tr(isNotYet ? 'leadsSubNotYet' : 'leadsSub')}
      loading={leads.length === 0}
    >
      {leads.map((lead, i) => (
        <div key={i} className={styles.lead}>
          <div className={styles.leadIco}>★</div>
          <div>
            {lead.strength && <p className={styles.leadStrength}>{lead.strength}</p>}
            {lead.how_to_sell && (
              <div className={styles.sell}>
                <b>{tr('howToSell')}</b>
                {lead.how_to_sell}
              </div>
            )}
          </div>
        </div>
      ))}
    </Section>
  );

  const renderConcerns = () => (concerns.length > 0 || !done) && (
    <Section
      key="concerns"
      title={tr('concernsTitle')}
      count={concerns.length}
      sub={tr(`concernsSub${isNotYet ? 'NotYet' : (verdict === 'STRETCH' ? 'Stretch' : '')}`)}
      loading={concerns.length === 0}
    >
      {concerns.map((concern, i) => (
        <div key={i} className={styles.concern}>
          <div className={styles.concernRow}>
            {(concern.type === 'REAL' || concern.type === 'BRIDGEABLE') && (
              <span className={cn(styles.tag, concern.type === 'BRIDGEABLE' ? styles.tagBridge : styles.tagReal)}>
                {tr(concern.type === 'BRIDGEABLE' ? 'tagBridge' : 'tagReal')}
              </span>
            )}
            {concern.gap && <p className={styles.gap}>{concern.gap}</p>}
          </div>
          {concern.action && <p className={styles.act}>{concern.action}</p>}
        </div>
      ))}
    </Section>
  );

  // practical points the recruiter cannot settle from the profile (salary vs.
  // the offer, location/work mode, availability) — usually empty, so it renders
  // only once an item has arrived instead of showing a skeleton that vanishes
  const renderOpenPoints = () => openPoints.length > 0 && (
    <Section
      key="openPoints"
      title={tr('openPointsTitle')}
      count={openPoints.length}
      sub={tr('openPointsSub')}
    >
      {openPoints.map((item, i) => (
        <div key={i} className={styles.concern}>
          {item.point && <p className={styles.gap}>{item.point}</p>}
          {item.action && <p className={styles.act}>{item.action}</p>}
        </div>
      ))}
    </Section>
  );

  const renderDevelopment = () => (development.length > 0 || !done) && (
    <Section
      key="development"
      title={tr(isNotYet ? 'devTitleNotYet' : 'devTitleStretch')}
      count={development.length}
      sub={tr(isNotYet ? 'devSubNotYet' : 'devSubStretch')}
      loading={development.length === 0}
    >
      {development.map((step, i) => (
        <div key={i} className={styles.step}>
          <div className={styles.stepMarker}><span className={styles.stepNum}>{i + 1}</span></div>
          <div className={styles.stepBody}>
            <p className={styles.stepWhat}>
              {step.what}
              {step.horizon && <span className={styles.horizon}>{step.horizon}</span>}
            </p>
            {step.why && <p className={styles.stepWhy}>{step.why}</p>}
            {step.how && (
              <div className={styles.stepHow}>
                <b>{tr('howTo')}</b>
                {step.how}
              </div>
            )}
          </div>
        </div>
      ))}
      {done && isNotYet && <p className={styles.recheck}>{tr('recheck')}</p>}
    </Section>
  );

  const renderInterviewPrep = () => (interviewPrep.length > 0 || !done) && (
    <Section
      key="prep"
      title={tr('prepTitle')}
      count={interviewPrep.length}
      sub={tr('prepSub')}
      loading={interviewPrep.length === 0}
    >
      {interviewPrep.map((item, i) => (
        <details key={i} className={styles.qa}>
          <summary>
            <span className={styles.q}>{i + 1}</span>
            {item.question}
            <span className={styles.chev}>▾</span>
          </summary>
          {(item.good_answer_contains || []).length > 0 && (
            <div className={styles.ans}>
              <b>{tr('goodAnswer')}</b>
              <ul>
                {item.good_answer_contains.map((point, j) => (
                  <li key={j}>{point}</li>
                ))}
              </ul>
            </div>
          )}
        </details>
      ))}
    </Section>
  );

  const renderTip = () => (r.application_tip || !done) && (
    <Section
      key="tip"
      tipStyle
      title={tr('tipTitle')}
      sub={tr('tipSub')}
      loading={!r.application_tip}
    >
      <p className={styles.tipBody}>{r.application_tip}</p>
    </Section>
  );

  // sections appear in the verdict-specific order of the final layout;
  // NOT_YET drops interview prep + application tip and leads with the plan.
  // Before the verdict resolves the section set is unknown — show generic
  // placeholders so the panel has its shape from the very first render.
  let sections = [];
  if (injectionDetected) {
    sections = [];
  } else if (verdict) {
    sections = isNotYet
      ? [renderConcerns(), renderOpenPoints(), renderDevelopment(), renderLeads()]
      : [
        renderLeads(),
        renderConcerns(),
        renderOpenPoints(),
        verdict === 'STRETCH' && renderDevelopment(),
        renderInterviewPrep(),
        renderTip(),
      ];
  } else if (!done) {
    sections = ['placeholder-1', 'placeholder-2'].map(key => (
      <section key={key} className={styles.sec}>
        <div className={styles.secHead}><span className={styles.titleSkeleton} /></div>
        <Skeleton />
      </section>
    ));
  }

  return (
    <div className={cn(styles.panel, injectionDetected ? styles.injection : (verdict && styles[VERDICT_CLASS[verdict]]))}>
      <div className={styles.panelHeader}>
        <div>
          <h4 className={styles.panelTitle}>
            <i className="la la-user-tie" />
            {tr('modalTitle')}
          </h4>
          <p className={styles.panelSubtitle}>{tr('modalSubtitle')}</p>
        </div>
        {onClose && (
          <CloseButton onClick={onClose}>{t('common:linkButton.close')}</CloseButton>
        )}
      </div>
      <div className={styles.headArea}>
        <span className={cn(styles.verdictChip, { [styles.verdictPending]: !verdict && !injectionDetected })}>
          {injectionDetected
            ? t('common:promptInjection.userReview.verdictError')
            : (verdict ? tr(`verdict.${verdict}`) : tr('generating'))}
        </span>
        <div className={styles.headBody}>
          {headline && (
            <div className={styles.scoreBlock}>
              <div className={styles.scoreCircle}>
                <Score id={`review-${jobId}`} score={score} type="your" />
              </div>
              <p className={styles.scoreLabel}>{tr('scoreLabel')}</p>
              <p className={styles.scoreHint}>{tr('scoreHint')}</p>
            </div>
          )}
          <div className={styles.headMain}>
            {headline
              ? <h2 className={styles.headline}>{headline}</h2>
              : !done && <span className={styles.titleSkeleton} />}
            {recruiterView
              ? <p className={styles.recruiterView}>{recruiterView}</p>
              : !done && !injectionDetected && (
                <div className={styles.loadingBox}>
                  <p className={styles.loadingText}>{tr('loading')}</p>
                  <LoadingSteps />
                </div>
              )}
            {injectionDetected && injectionEvidence && (
              <blockquote className={styles.injectionEvidence}>
                <span className={styles.injectionEvidenceLabel}>{t('common:promptInjection.evidence')}</span>
                {injectionEvidence}
              </blockquote>
            )}
            {injectionDetected && (
              <p className={styles.falsePositive}>
                {t('common:promptInjection.userReview.falsePositive', { context: gender })}{' '}
                <Link to={routes.contact}>{t('common:promptInjection.userReview.contactUs')}</Link>.
              </p>
            )}
          </div>
        </div>
      </div>
      {scoreDims && headline && !injectionDetected && (
        <Section title={tr('dimsTitle')} sub={tr('dimsSub')}>
          <ScoreDims
            dims={scoreDims}
            showValue
            className={styles.dims}
            getLabel={key => t(key === 'context' ? 'common:score.dimShort.context' : `common:score.dim.${key}`)}
            getTooltip={(key, value) => (
              <>
                <strong>{t(`common:score.dim.${key}`)}: {value}%</strong>
                <br />
                {t(`common:score.dimTooltip.${key}`)}
              </>
            )}
          />
        </Section>
      )}
      {sections}
      {done && !injectionDetected && <p className={styles.footNote}>{tr('note')}</p>}
    </div>
  );
};

RecruiterReview.propTypes = {
  review: PropTypes.object,
  done: PropTypes.bool,
  jobId: PropTypes.string.isRequired,
  score: PropTypes.number,
  scoreDims: PropTypes.object,
  gender: PropTypes.string,
  injectionDetected: PropTypes.bool,
  injectionEvidence: PropTypes.string,
  onClose: PropTypes.func,
};

RecruiterReview.defaultProps = {
  review: null,
  done: false,
  score: 0,
  scoreDims: null,
  gender: null,
  injectionDetected: false,
  injectionEvidence: null,
  onClose: null,
};

export default RecruiterReview;
